/**
 * Bazaarvoice Reviews View More Component
 * Handles collapsible reviews container with "View More" / "View Less" functionality
 */

(function() {
  // Prevent duplicate registration
  if (customElements.get('bv-reviews-view-more')) {
    return;
  }

  class BvReviewsViewMore extends HTMLElement {
    constructor() {
      super();
      
      this.container = null;
      this.btnMore = null;
      this.btnLess = null;
      this.isExpanded = false;
      this.observer = null;
      this.contentFitsCollapsed = false;
      this.contentCheckInterval = null;
      this.maxCheckAttempts = 20; // Max 20 attempts (10 seconds total)
      this.checkAttempts = 0;
      
      // Bound event handler references for proper cleanup
      this._onExpand = null;
      this._onCollapse = null;
    }

    connectedCallback() {
      this.container = this.querySelector('[data-bv-reviews-container]');
      this.btnMore = this.querySelector('[data-bv-view-more-btn]');
      this.btnLess = this.querySelector('[data-bv-view-less-btn]');
      
      if (this.container && this.btnMore) {
        this.init();
      }
    }

    init() {
      // Reset polling state for potential reconnection
      this.checkAttempts = 0;

      // Ensure initial state is collapsed
      this.ensureCollapsedState();
      
      // Create bound handler references (only once)
      if (!this._onExpand) {
        this._onExpand = this.handleExpandClick.bind(this);
      }
      if (!this._onCollapse) {
        this._onCollapse = this.handleCollapseClick.bind(this);
      }
      
      // Bind event listeners for button clicks
      if (this.btnMore) {
        this.btnMore.addEventListener('click', this._onExpand);
      }
      
      if (this.btnLess) {
        this.btnLess.addEventListener('click', this._onCollapse);
      }
      
      // Start polling for content load
      this.startContentCheck();
    }

    startContentCheck() {
      // Poll every 500ms until content is loaded or max attempts reached
      this.contentCheckInterval = setInterval(() => {
        this.checkAttempts++;
        
        const contentLoaded = this.checkContentHeight();
        
        // Stop polling if content loaded or max attempts reached
        if (contentLoaded || this.checkAttempts >= this.maxCheckAttempts) {
          this.stopContentCheck();
        }
      }, 500);
    }

    stopContentCheck() {
      if (this.contentCheckInterval) {
        clearInterval(this.contentCheckInterval);
        this.contentCheckInterval = null;
      }
    }

    ensureCollapsedState() {
      if (!this.isExpanded) {
        this.container.classList.add('is-collapsed');
        this.container.classList.remove('is-expanded');
        this.btnMore.classList.remove('is-hidden');
        if (this.btnLess) {
          this.btnLess.classList.add('is-hidden');
        }
      }
    }

    checkContentHeight() {
      if (!this.container || this.isExpanded) return true;
      
      const collapsedHeight = parseInt(
        getComputedStyle(this.container).getPropertyValue('--bv-collapsed-height')
      ) || 650;
      
      const contentHeight = this.container.scrollHeight;
      
      // Only proceed if content has actually loaded (has some minimum height)
      const minContentHeight = 100;
      if (contentHeight < minContentHeight) {
        return false; // Content hasn't loaded yet
      }
      
      // Content has loaded
      if (contentHeight <= collapsedHeight) {
        // Content fits, expand and hide buttons
        this.contentFitsCollapsed = true;
        this.expand();
        this.btnMore.classList.add('is-hidden');
        if (this.btnLess) {
          this.btnLess.classList.add('is-hidden');
        }
      } else {
        // Content is taller, ensure collapsed state
        this.ensureCollapsedState();
      }
      
      return true; // Content has been checked
    }

    handleExpandClick() {
      this.expand();
      this.btnMore.classList.add('is-hidden');
      if (this.btnLess && !this.contentFitsCollapsed) {
        this.btnLess.classList.remove('is-hidden');
      }
    }

    handleCollapseClick() {
      this.collapse();
      if (this.btnLess) {
        this.btnLess.classList.add('is-hidden');
      }
      this.btnMore.classList.remove('is-hidden');
      
      // Scroll back to the top of the reviews section
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    expand() {
      this.isExpanded = true;
      this.container.classList.remove('is-collapsed');
      this.container.classList.add('is-expanded');
      this.btnMore.setAttribute('aria-expanded', 'true');
    }

    collapse() {
      this.isExpanded = false;
      this.container.classList.remove('is-expanded');
      this.container.classList.add('is-collapsed');
      this.btnMore.setAttribute('aria-expanded', 'false');
    }

    disconnectedCallback() {
      this.stopContentCheck();
      
      // Remove event listeners using stored references
      if (this.btnMore && this._onExpand) {
        this.btnMore.removeEventListener('click', this._onExpand);
      }
      if (this.btnLess && this._onCollapse) {
        this.btnLess.removeEventListener('click', this._onCollapse);
      }
    }
  }

  customElements.define('bv-reviews-view-more', BvReviewsViewMore);
})();
